import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileUpload } from 'src/app/interface/buildings.interface';

export enum FileTypeIcon {
  pdf = 'picture_as_pdf',
  image = 'image',
  draft = 'drafts'
}

@Component({
  selector: 'common-file-uploader-app',
  templateUrl: './file-uploader-app.component.html',
  styleUrls: ['./file-uploader-app.component.scss']
})
export class FileUploaderAppComponent implements OnInit {
  @Input() label: string = 'Label text goes here';
  @Input() acceptedFormats: string = '.jpeg, .jpg, .png';
  @Input() maxSizeText: string = '(1MB)';
  @Input() maxSizeInBytes: number = 1048576;
  @Input() imagesToShow: FileUpload[] = [];
  @Input() isMultipleFiles: boolean = true;

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesRemoved = new EventEmitter<File[]>();


  uploadedFiles: Array<{ name: string, size: number, file: File, fileTypeIcon: FileTypeIcon }> = [];
  fileTypeIcon: FileTypeIcon = FileTypeIcon.draft;
  hasError: boolean = false;
  errorMessage: string = '';

  constructor(private translateService: TranslateService) { }

  ngOnInit(): void {
    if (this.imagesToShow && this.imagesToShow.length > 0) {
      this.uploadedFiles = this.imagesToShow.map(image => ({
        name: image.filename,
        size: image.size,
        file: new File([], image.filename),
        fileTypeIcon: this.getFileTypeIcon(image.filename)
      }));
    }
  }

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      if (!this.isMultipleFiles && files.length > 1) {
        this.hasError = true;
        this.errorMessage = this.translateService.instant('common-components.fileUploader.errors.multipleFilesNotAllowed');
        event.target.value = '';
        return;
      }

      this.uploadedFiles = [];
      let hasValidationError = false;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const validation = this.validateFile(file);

        if (!validation.isValid) {
          hasValidationError = true;
          this.hasError = true;
          this.errorMessage = validation.errorMessage;
          break;
        }

        this.uploadedFiles.push({
          name: file.name,
          size: file.size,
          file: file,
          fileTypeIcon: this.getFileTypeIcon(file.name)
        });
      }

      if (!hasValidationError) {
        this.hasError = false;
        this.errorMessage = '';
        this.filesSelected.emit(Array.from(files));
      } else {
        this.uploadedFiles = [];
        event.target.value = '';
      }
    }
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.filesRemoved.emit(this.uploadedFiles.map(file => file.file));
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0.00 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  truncateFileName(fileName: string): string {
    if (fileName.length > 10) {
      return fileName.substring(0, 20) + '...';
    }
    return fileName;
  }

  triggerFileInput(fileInput: HTMLInputElement): void {
    fileInput.click();
  }

  getFileTypeIcon(fileName: string): FileTypeIcon {
    if (fileName.endsWith('.pdf')) {
      return FileTypeIcon.pdf;
    }
    if (fileName.endsWith('.jpg') || fileName.endsWith('.jpeg') || fileName.endsWith('.png')) {
      return FileTypeIcon.image;
    }
    return FileTypeIcon.draft;
  }

  validateFile(file: File): { isValid: boolean, errorMessage: string } {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const acceptedFormatsArray = this.acceptedFormats
      .split(',')
      .map(format => format.trim().toLowerCase());

    if (!acceptedFormatsArray.includes(fileExtension)) {
      return {
        isValid: false,
        errorMessage: this.translateService.instant('common-components.fileUploader.errors.invalidFormat')
      };
    }

    if (file.size > this.maxSizeInBytes) {
      return {
        isValid: false,
        errorMessage: this.translateService.instant('common-components.fileUploader.errors.fileTooLarge', { maxSizeText: this.maxSizeText })
      };
    }

    return {
      isValid: true,
      errorMessage: ''
    };
  }
}
